import React, { useEffect, useState } from 'react';
import { fetchAdminLogs, fetchUserLogs, searchAdmins } from '../services/logService';
import { useCachedPermissions } from '../hooks/useCachedPermissions';
import PageNavigator from './PageNavigator';
import FilterDialog from './FilterDialog';
import styles from './TicketsTabContent.module.css';

const ITEMS_PER_PAGE = 16;

const LogsTabContent = ({ selectedTab }) => {
  // Get logs permissions (must be called at the top)
  const { hasShowAdminLogs, hasShowUserLogs } = useCachedPermissions();

  // All useState hooks must be called before any conditional returns
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [showFilterDialog, setShowFilterDialog] = useState(false);
  const [filterValues, setFilterValues] = useState({});
  const [adminSearchResults, setAdminSearchResults] = useState([]);
  const [adminSearchLoading, setAdminSearchLoading] = useState(false);

  // Function declarations before useEffect hooks
  const fetchAdminLogsData = async (page = 1, filters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchAdminLogs(page, ITEMS_PER_PAGE, filters);
      if (response.data && response.data.data) {
        setLogs(response.data.data);
        setTotalPages(response.data.last_page || 1);
        setTotalResults(response.data.results || 0);
      } else {
        setLogs([]);
        setTotalPages(1);
        setTotalResults(0);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch logs');
      setLogs([]);
      setTotalPages(1);
      setTotalResults(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserLogsData = async (page = 1, filters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchUserLogs(page, ITEMS_PER_PAGE, filters);
      if (response.data && response.data.data) {
        setLogs(response.data.data);
        setTotalPages(response.data.last_page || 1);
        setTotalResults(response.data.results || 0);
      } else {
        setLogs([]);
        setTotalPages(1);
        setTotalResults(0);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch logs');
      setLogs([]);
      setTotalPages(1);
      setTotalResults(0);
    } finally {
      setLoading(false);
    }
  };

  // All useEffect hooks must also be called before any conditional returns
  useEffect(() => {
    if (selectedTab === 'logs-user') {
      fetchUserLogsData(currentPage, filterValues);
    } else if (selectedTab === 'logs-admin') {
      fetchAdminLogsData(currentPage, filterValues);
    }
  }, [selectedTab, currentPage, filterValues]);

  // Reset current page when switching tabs
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedTab]);

  // Check permissions based on selected tab (after all hooks)
  if (selectedTab === 'logs-admin' && !hasShowAdminLogs) {
    return (
      <div className={styles.permissionDeniedMessage}>
        <h3>Access Denied</h3>
        <p>You do not have permission to view admin logs.</p>
      </div>
    );
  }

  if (selectedTab === 'logs-user' && !hasShowUserLogs) {
    return (
      <div className={styles.permissionDeniedMessage}>
        <h3>Access Denied</h3>
        <p>You do not have permission to view user logs.</p>
      </div>
    );
  }

  // If accessing logs main tab without any permissions
  if (selectedTab === 'logs' && !hasShowAdminLogs && !hasShowUserLogs) {
    return (
      <div className={styles.permissionDeniedMessage}>
        <h3>Access Denied</h3>
        <p>You do not have permission to view logs.</p>
      </div>
    );
  }

  const handleFilter = () => {
    console.log('Filter button clicked - opening dialog');
    setShowFilterDialog(true);
  };

  const handleFilterApply = (newFilterValues) => {
    console.log('Filter applied:', newFilterValues);
    setFilterValues(newFilterValues);
    setCurrentPage(1); // Reset to first page when applying filters

    // Fetch logs with the new filter values
    if (selectedTab === 'logs-user') {
      fetchUserLogsData(1, newFilterValues);
    } else if (selectedTab === 'logs-admin') {
      fetchAdminLogsData(1, newFilterValues);
    }
  };

  const handleFilterClose = () => {
    setShowFilterDialog(false);
  };

  const handleResetFilter = () => {
    console.log('Reset filter clicked - making API request to clear filters');
    setFilterValues({});
    setCurrentPage(1);
    setAdminSearchResults([]); // Clear search results

    // Always make API request to fetch unfiltered data
    if (selectedTab === 'logs-user') {
      fetchUserLogsData(1, {});
    } else if (selectedTab === 'logs-admin') {
      fetchAdminLogsData(1, {});
    }
  };

  // Debounce function for search
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(null, args), delay);
    };
  };

  // Admin search function
  const searchAdminsFunction = async (searchTerm) => {
    // Don't search if term is empty or too short
    if (!searchTerm || searchTerm.trim().length < 2) {
      setAdminSearchResults([]);
      setAdminSearchLoading(false);
      return;
    }

    setAdminSearchLoading(true);
    try {
      console.log('Searching admins with term:', searchTerm);
      const response = await searchAdmins(searchTerm.trim());
      console.log('Admin search response:', response);

      if (response.data && Array.isArray(response.data)) {
        setAdminSearchResults(response.data);
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        setAdminSearchResults(response.data.data);
      } else if (Array.isArray(response)) {
        setAdminSearchResults(response);
      } else {
        setAdminSearchResults([]);
      }
    } catch (error) {
      console.error('Error searching admins:', error);
      setAdminSearchResults([]);
    } finally {
      setAdminSearchLoading(false);
    }
  };

  const debouncedAdminSearch = debounce(searchAdminsFunction, 300);

  // Define filter fields
  const filterFields = [
    {
      type: 'date',
      name: 'date',
      label: 'Date',
      placeholder: 'Select date'
    },
    {
      type: 'searchable-select',
      name: 'adminName',
      label: 'Admin Name',
      placeholder: 'Search for an admin...',
      onSearch: debouncedAdminSearch,
      searchOptions: adminSearchResults,
      loading: adminSearchLoading,
      displayKey: 'name',
      valueKey: '_id'
    }
  ];

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <div className={styles.ticketsContainer}>
      <div className={styles.headerContainer}>
        <div className={styles.headerLeft}>
          <h2 className={styles.headerTitle}>Logs</h2>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.actionButton} onClick={handleFilter}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46" />
            </svg>
            Filter
          </button>
          <button className={styles.actionButton} onClick={handleResetFilter}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
              <path d="M21 3v5h-5" />
              <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
              <path d="M3 21v-5h5" />
            </svg>
            Reset
          </button>
        </div>
      </div>

      {selectedTab === 'logs' ? (
        <div className={styles.selectSubTabMessage}>
          <h3>Select Log Type</h3>
          <p>Please select "Admin" or "User" logs to view.</p>
        </div>
      ) : loading ? (
        <div className={styles.loadingMessage}>
          <h3>Loading...</h3>
          <p>Fetching logs...</p>
        </div>
      ) : error ? (
        <div className={styles.errorMessage}>
          <h3>Error</h3>
          <p>{error}</p>
          <button onClick={() => {
            if (selectedTab === 'logs-admin') {
              fetchAdminLogsData(currentPage, filterValues)
            } else {
              fetchUserLogsData(currentPage, filterValues)
            }

          }} className={styles.retryButton}>
            Retry
          </button>
        </div>
      ) : logs.length === 0 ? (
        <div className={styles.noTicketsMessage}>
          <h3>No Logs</h3>
          <p>There are currently no logs to display.</p>
        </div>
      ) : (
        <>
          <div className={styles.tableContainer}>
            <table className={styles.ticketsTable}>
              <thead>
                <tr>
                  <th style={{ width: 60, maxWidth: 60 }}>#</th>
                  <th style={{ maxWidth: 200 }}>{selectedTab === 'logs-user' ? 'User' : 'Admin'}</th>
                  <th style={{ maxWidth: 300 }}>Action</th>
                  {selectedTab === 'logs-admin' && <th style={{ maxWidth: 150 }}>IP Address</th>}
                  <th style={{ maxWidth: 180 }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, idx) => (
                  <tr key={log._id} className={styles.tableRow}>
                    <td style={{ maxWidth: 60 }}>
                      <span className={styles.idValue}>{(currentPage - 1) * ITEMS_PER_PAGE + idx + 1}</span>
                    </td>
                    <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedTab === 'logs-user' ? (log.user || log.admin) : log.admin}</td>
                    <td style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.action}</td>
                    {selectedTab === 'logs-admin' && <td style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.ip_address}</td>}
                    <td style={{ maxWidth: 180 }}>
                      <span className={styles.dateValue}>{log.created_at}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <PageNavigator
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}

      {/* Filter Dialog */}
      <FilterDialog
        isOpen={showFilterDialog}
        onClose={handleFilterClose}
        onApply={handleFilterApply}
        title="Filter Logs"
        fields={filterFields}
        initialValues={filterValues}
      />
    </div>
  );
};

export default LogsTabContent; 